// prettier-ignore
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CtAttributesService } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-attributes.service';
import { CtNetworkElementType } from '@cloud-twin-v2/cloud-twin-shared/core/network-elements/ct-network-element-type';
import { DashboardCartAndSettings } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/dashboard-cart-and-settings';
import { SelectedWorkspaceService } from '@cloud-twin-v2/cloud-twin-shared/selected-workspace.service';
import { InstancesRegisterService } from '@cloud-twin-v2/generated/services/instances-register.service';
import {
  CtWorkspaceDto,
  InstanceResponseDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { MasterOperatorService } from '@cloud-twin/master-operator/services/master-operator.service';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { WtrColors } from '@common-modules/shared/styles/wtr-colors';
import { GenericMapDataService } from '@common-modules/wlm-map/generic-map/services/generic-map-data.service';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { CreateDashboardWidgetCartComponent } from '../create-dashboard-widget-cart/create-dashboard-widget-cart.component';
import { CtNetworkElementFilters } from '../models/ct-network-element-filters';
import { DashboardWidgetTypes } from '../models/dashboard-widget-types';
import { SearchResultAttribute } from '../models/search-result-attribute';
import { SearchResultItem } from '../models/search-result-item';

class FilterOption {
  value: string;
  label: string;
  selected?: boolean;
  icon?: string;
}

const COMPONENT_SELECTOR = 'ct-create-dashboard-widget-form';

@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './create-dashboard-widget-form.component.html',
  styleUrl: './create-dashboard-widget-form.component.scss',
  standalone: false,
})
export class CreateDashboardWidgetFormComponent implements OnInit {
  @ViewChild(CreateDashboardWidgetCartComponent)
  createDashboardWidgetCartComponent: CreateDashboardWidgetCartComponent;

  private _initialCartSelection: DashboardCartAndSettings;

  public get initialCartSelection(): DashboardCartAndSettings {
    return this._initialCartSelection;
  }
  @Input() set initialCartSelection(value: DashboardCartAndSettings) {
    this._initialCartSelection = value;
    this.initFromModel();
  }
  @Input() disableAddToDashboard = false;
  private _isSettingsComponent = false;
  public get isSettingsComponent(): boolean {
    return this._isSettingsComponent;
  }
  @Input() set isSettingsComponent(value: boolean) {
    this._isSettingsComponent = value;

    setTimeout(() => {
      this.createDashboardWidgetCartComponent.onApplySettingsComponentConfiguration();
    });
  }

  @Output() isValidCart = new EventEmitter<boolean>();
  @Output() cartSelectionSubmit = new EventEmitter<DashboardCartAndSettings>();

  readonly widgetTypeIconColor = WtrColors.PrimaryColor;
  readonly itemSize = 36;

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _selectedWorkspaceService = inject(SelectedWorkspaceService);
  private readonly _ctAttributesService = inject(CtAttributesService);
  private readonly _genericMapDataService = inject(GenericMapDataService);
  private readonly _instanceRegisterService = inject(InstancesRegisterService);
  private readonly _masterOperatorService = inject(MasterOperatorService);

  readonly T_SCOPE = `${AppModules.RightPanel}.${COMPONENT_SELECTOR}`;
  readonly widgetTypes = [
    { id: DashboardWidgetTypes.AttributeChart, labelKey: 'att-chart', icon: 'bar_chart' },
    { id: DashboardWidgetTypes.Grid, labelKey: 'grid', icon: 'grid_on' },
    { id: DashboardWidgetTypes.Text, labelKey: 'text', icon: 'notes' },
  ];

  scenarioOptions: FilterOption[] = [];
  networkElementTypeOptions: FilterOption[] = [];
  selectedWidgetType: DashboardWidgetTypes;
  selectedAttributes: SearchResultAttribute[] = [];
  selectedMeasurements: SearchResultItem[] = [];
  filters: CtNetworkElementFilters = {};
  showWidgetTypeSelection = true;
  networkElementTypes: CtNetworkElementType[];
  defaultIconSize = 20;
  existingNetworkElements: string[];

  ngOnInit(): void {
    this._selectedWorkspaceService.selectedWorkspace$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((workspace) => {
        this.buildScenarioOptions(workspace);
      });

    this._ctAttributesService
      .getNetworkElementTypes()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((networkElementTypes) => {
        this.networkElementTypes = networkElementTypes;
      });

    this._ctAttributesService
      .getIconsByNEType()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((iconsByType) => {
        this.networkElementTypes.forEach((netType) => {
          const icons =
            iconsByType.get(netType.networkElementTypeId) ||
            iconsByType.get(netType.networkElementTypeName);
          if (icons && icons.length > 0) {
            netType.icon = icons[0].iconSvg;
          }
        });
      });

    this.buildNetworkElementOptions(this.networkElementTypes);
    this.listenMapTimeStep();
  }

  initFromModel(): void {
    if (!this.initialCartSelection) {
      return;
    }

    this.selectedWidgetType = this.initialCartSelection.widgetType;
    this.showWidgetTypeSelection = false;

    setTimeout(() => {
      this.createDashboardWidgetCartComponent.initFromModel(this.initialCartSelection);
      this.existingNetworkElements = this.extractUniqueNetworkElement(this.initialCartSelection);
    });
  }

  buildCartAndSettings(): Observable<DashboardCartAndSettings> {
    return this.createDashboardWidgetCartComponent.buildCartAndSettings();
  }

  selectWidgetType(type: DashboardWidgetTypes): void {
    this.selectedWidgetType = type;
  }

  onFiltersChange(): void {
    const networkElementTypeIds = this.networkElementTypeOptions
      .filter((item) => item.selected)
      .map((item) => item.value);

    const scenarioIds = this.scenarioOptions
      .filter((item) => item.selected)
      .map((item) => item.value);

    this.filters = {
      networkElementTypeIds,
      scenarioIds,
    };
  }

  onAddAttributesToCart(attributes: SearchResultAttribute[]): void {
    this.createDashboardWidgetCartComponent.addAttributes(attributes);
  }

  onAddMeasurementToCart(measurement: SearchResultItem): void {
    this.createDashboardWidgetCartComponent.addMeasurement(measurement);
  }

  onCartSelectionSubmit(cartSelection: DashboardCartAndSettings): void {
    this.cartSelectionSubmit.emit(cartSelection);
  }

  onNetworkElementChange(networkElements: string[]): void {
    this.existingNetworkElements = [...networkElements];
  }

  private listenMapTimeStep() {
    this._genericMapDataService
      .listenCurrentTimeStep$()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((step) => {
        if (!step) {
          return;
        }
        setTimeout(() => {
          this.createDashboardWidgetCartComponent.setCurrentTimeStep(step);
        });
      });
  }

  private buildScenarioOptions(workspace: CtWorkspaceDto): void {
    const instanceRequests = workspace.workspaceConnectSettings.map((item) => {
      if (!item.instanceName) {
        return this.buildInstanceName(item.instanceId).pipe(
          map((instanceName) => ({
            ...item,
            instanceName,
          }))
        );
      } else {
        return of(item);
      }
    });

    forkJoin(instanceRequests).subscribe((settingsWithNames) => {
      this.scenarioOptions = settingsWithNames.map(
        (item) =>
          ({
            value: item.scenarioId,
            label: `${item.instanceName ?? ''} / ${item.snapshotName ?? ''} / ${item.scenarioName}`,
          } as FilterOption)
      );
    });
  }

  private extractUniqueNetworkElement(cartSelection: DashboardCartAndSettings): string[] {
    if (cartSelection && Array.isArray(cartSelection.items)) {
      const titles = cartSelection.items
        .map((item) => {
          const title = item.filtrableType.title;
          return title ? title : undefined;
        })
        .filter((title) => !!title);
      return Array.from(new Set(titles));
    } else {
      return [];
    }
  }

  private buildNetworkElementOptions(networkElementTypes: CtNetworkElementType[]): void {
    this.networkElementTypeOptions = networkElementTypes.map(
      (net) =>
        ({
          value: net.networkElementTypeId,
          label: net.networkElementTypeName,
          icon: net.icon,
        } as FilterOption)
    );
  }

  private buildInstanceName(instanceId: string): Observable<string> {
    return this._instanceRegisterService.getInstance(instanceId).pipe(
      map((instance) => {
        const name = this._masterOperatorService.instanceReadableLabel(
          instance as Required<InstanceResponseDto>
        );
        return name ?? '';
      }),
      catchError(() => of(''))
    );
  }
}
